import { isoZ, z } from 'my-zod'
import { enumOptionsToObject } from '../../internal/enum-helpers'

export const stripeChargeRiskLevelOptions = ['normal', 'highest', 'elevated', 'not_assessed', 'unknown', 'blocked'] as const
export type stripeChargeRiskLevel = (typeof stripeChargeRiskLevelOptions)[number]
export const stripeChargeRiskLevel = z.enum(stripeChargeRiskLevelOptions)

export const ManualCreditAdjustmentReasonCodeOptions = [
  'ReferralBonus',
  'CreditShareSupport',
  'PolarisPromoBillback',
  'ConciergeBooking',
  'NoShow',
  'Promo',
  'EmployeeComp',
  'Goodwill',
  'Refund',
  'Ambassador Ride',
  'Other'
] as const
export type ManualCreditAdjustmentReasonCode = (typeof ManualCreditAdjustmentReasonCodeOptions)[number]
export const ManualCreditAdjustmentReasonCodes = enumOptionsToObject(ManualCreditAdjustmentReasonCodeOptions)

export const platformOptions = ['Polaris', 'Select', 'Viator'] as const
export const externalPlatformOptions = ['Select', 'Viator'] as const

export const transferMetadata = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('ManualAdjustment'),
    reason: z.string().min(5).nonempty(),
    reasonCode: z.enum(ManualCreditAdjustmentReasonCodeOptions),
    userId: z.number().nullable()
  }),
  z.object({
    type: z.literal(
      'CreditShare'
    ) /** for moving credits between two customers as a result of a credit share action by one of the customers */
  }),
  z.object({
    type: z.literal('StripeConnectPayment') /** for cash transfers to a customer as a result of a stripe charge  */,
    stripeId: z.string(),
    status: z.string(),
    feeAmount: z.number().optional(),
    platformBalanceTransactionId: z.string(), //type payment
    connectBalanceTransactionId: z.string() //type transfer
  }),
  z.object({
    type: z.literal('StripeConnectPaymentReversal'),
    stripeId: z.string(),
    status: z.string(),
    platformBalanceTransactionId: z.string(), //type payment_refund
    connectBalanceTransactionId: z.string() //type transfer_refund
  }),
  z.object({
    type: z.literal('StripeCharge'),
    stripeId: z.string(), //Stripe payment intent (pi_)
    status: z.string(),
    risk: z
      .object({
        score: z.number(),
        level: stripeChargeRiskLevel
      })
      .nullish(),
    createdDate: isoZ.instant() // created date of the charge in stripe
  }),
  z.object({
    type: z.literal('StripeChargeCapture'),
    stripeId: z.string(), //Stripe charge (ch_)
    status: z.string(),
    feeAmount: z.number().optional(),
    platformBalanceTransactionId: z.string() //type charge or payment
  }),
  z.object({
    type: z.literal('StripeChargeRefund'),
    stripeId: z.string(), //refund (re_)
    status: z.string()
  }),
  z.object({
    type: z.literal('StripeChargeRefundReversal'),
    stripeId: z.string(), //Failed Stripe Refund (re_)
    status: z.string()
  }),
  z.object({
    type: z.literal('StripeChargeCaptureRefund'),
    stripeId: z.string(), //Stripe Refund (re_)
    status: z.string(),
    reason: z.enum(['partialOrNoCapture', 'other']).nullish(),
    platformBalanceTransactionId: z.string() //type refund or payment_refund
  }),
  z.object({
    type: z.literal('StripeChargeCaptureRefundReversal'),
    stripeId: z.string(), //Failed Stripe Refund (re_)
    status: z.string(),
    platformBalanceTransactionId: z.string() //type refund_failure
  }),
  z.object({
    type: z.literal('RequestedStripeRefundAllocation'),
    paymentMethodId: z.string().nullable(),
    requestedDate: isoZ.instant()
  }),
  z.object({
    type: z.literal('RequestedStripeRefundAllocationReversal'),
    processedStripeRefundId: z.string().min(1)
  }),
  z.object({
    type: z.literal(
      'SubscriptionRenewalPayment'
    ) /** for transfers to a subscription to indicate the full amount owed for a subscription renewal */
  }),
  z.object({
    type: z.literal(
      'SubscriptionRenewal'
    ) /** for transfers where cash goes into the credit bank and credits come out because the customer renewed their subscription */,
    stripeId: z.string()
  }),
  z.object({
    type: z.literal('SubscriptionSignupBonus')
  }),
  z.object({
    type: z.literal('CreditMigration')
  }),
  z.object({
    type: z.literal('OrderLinePayment') /** for transfers from the customer to an order line */,
    platform: z.enum(platformOptions),
    orderLineType: z.enum([
      'Reservation',
      'Merchandise',
      'RentalEquipment',
      'CancellationFee',
      'DamageFee',
      'AdHoc',
      'WeatherGuarantee',
      'RideInsurance',
      'GiftCard'
    ]),
    name: z.string()
  }),
  z.object({
    type: z.literal('OrderLinePaymentReversal')
  }),
  z.object({
    type: z.literal('OrderLineTax') /** the tax associated with an order line payment */,
    unit: z.enum(['flat', 'percentage']),
    amount: z.number(),
    title: z.string(),
    id: z.number()
  }),
  z.object({
    type: z.literal('OrderLineTaxReversal')
  }),
  z.object({
    type: z.literal('TaxRemittal') /** for the outfitter to remit tax */,
    unit: z.enum(['flat', 'percentage']),
    amount: z.number(),
    title: z.string(),
    id: z.number()
  }),
  z.object({
    type: z.literal('TaxRemittalReversal') /** for the outfitter to remit tax */
  }),
  z.object({
    type: z.literal('Discount'),
    discountId: z.string()
  }),
  z.object({
    type: z.literal('DiscountReversal')
  }),
  z.object({
    type: z.literal('OrderLineDiscount'),
    discountShortId: z.string(),
    discountType: z.enum(['AdHoc', 'Promotion']),
    promotionShortId: z.string().nullable(),
    promotionVersionId: z.number().nullable(),
    discountUnit: z.enum(['fixedAmountOffTotal', 'percentOffEach', 'percentOffSingle']),
    discountAmount: z.number(),
    discountProvidedBy: z.enum(['Outfitter', 'Polaris'])
  }),
  z.object({
    type: z.literal('OrderLineDiscountReversal')
  }),
  z.object({
    type: z.literal('OrderLineDiscountReimbursement')
  }),
  z.object({
    type: z.literal('OrderLineDiscountReimbursementReversal')
  }),
  z.object({
    type: z.literal('ExternalPayment'),
    platform: z.enum(externalPlatformOptions)
  }),
  z.object({
    type: z.literal('ExternalPaymentReversal')
  }),
  z.object({
    type: z.literal('PhysicalPayment'),
    method: z.enum(['cash', 'check'])
  }),
  z.object({
    type: z.literal('PhysicalRefund')
  }),
  z.object({
    type: z.literal(
      'CreditPurchase'
    ) /** for transfers where cash goes into the credit bank and credits come out because the customer purchased credits */
  }),
  z.object({
    type: z.literal(
      'CreditRedemption'
    ) /** for transfers where credits go into the credit bank and cash comes out because the customer is purchasing an order line */
  }),
  z.object({
    type: z.literal('CreditRedemptionReversal')
  }),
  z.object({
    type: z.literal('PlatformFee') /**, for transfers from an order line to PolarisPlatformFees */,
    amount: z.number(),
    unit: z.enum(['fixed', 'percentage']),
    feeType: z.enum([
      'creditCardFee',
      'partnershipFee',
      'onlineBookingFee',
      'nationalSiteCommission',
      'selectBookingFee',
      'rideInsuranceFee',
      'weatherGuaranteeFee',
      'chargebackFee',
      'vehiclePaymentPlanFee'
    ])
  }),
  z.object({
    type: z.literal('PlatformFeeReversal')
  }),
  z.object({
    type: z.literal('OutfitterShare') /** for transfers from an order line to the outfitter's account in our system */
  }),
  z.object({
    type: z.literal('OutfitterShareReversal')
  }),
  z.object({
    type: z.literal(
      'StripeTransfer'
    ) /** for transfers from an Outfitter's account in our system to their stripe connect account */,
    stripeId: z.string(),
    status: z.string(),
    platformBalanceTransactionId: z.string(), // type transfer
    connectBalanceTransactionId: z.string(), // type payment
    sourceTransaction: z.string().nullable()
  }),
  z.object({
    type: z.literal('StripeTransferReversal'),
    stripeId: z.string(),
    status: z.string(),
    platformBalanceTransactionId: z.string(), //type transfer_refund
    connectBalanceTransactionId: z.string() // type payment_refund
  }),
  z.object({
    type: z.literal('CreditExpiration'),
    reason: z.enum(['Cancellation'])
  }),
  z.object({
    type: z.literal('StripePayoutLine'),
    stripeId: z.string(),
    arrivalDate: isoZ.instant().optional(),
    platformBalanceTransactionPaidOut: z.string().nullable(),
    connectBalanceTransactionPaidOut: z.string().nullable()
  }),
  z.object({
    type: z.literal('StripePayoutLineReversal')
  }),
  z.object({
    type: z.literal('StripePayout'),
    stripeId: z.string(),
    platformBalanceTransactionId: z.string().nullable(),
    connectBalanceTransactionId: z.string().nullable()
  }),
  z.object({
    type: z.literal('StripePayoutReversal'),
    platformBalanceTransactionId: z.string().nullable(),
    connectBalanceTransactionId: z.string().nullable()
  }),
  z.object({
    type: z.literal('ManualPaymentToOutfitter')
  }),

  z.object({
    type: z.literal('Chargeback'), //occurs when funds are returned to payment method as a result of a dispute
    stripeId: z.string() // Stripe Dispute
  }),
  z.object({
    type: z.literal('ChargebackReversal')
  }),
  z.object({
    type: z.literal('PolarisChargebackWithdrawal'), //occurs when Stripe deducts the Chargeback amount from Polaris' account balance
    stripeId: z.string(), // Stripe Dispute
    platformBalanceTransactionId: z.string(), //type adjustment
    feeAmount: z.number().optional()
  }),
  z.object({
    type: z.literal('PolarisChargebackWithdrawalReversal'),
    platformBalanceTransactionId: z.string() //type adjustment
  }),
  z.object({
    type: z.literal('OutfitterChargebackWithdrawal'), //occurs when Polaris withdraws funds from the Outfitter to cover their own Chargeback withdrawal
    disputeId: z.string()
  }),
  z.object({
    type: z.literal('OutfitterChargebackWithdrawalReversal')
  }),
  z.object({
    type: z.literal('CustomerChargebackWithdrawal') //if customer has outstanding balance that cannot be refunded due to a dispute, that balance is used by outfitter to cover their dispute liability
  }),
  z.object({
    type: z.literal('CustomerChargebackWithdrawalReversal')
  }),
  z.object({
    type: z.literal('PlatformFeeSurrender'), //after withdrawing funds for a chargeback, Polaris may optionally return platform fees to the outfitter
    feeType: z.enum([
      'partnershipFee',
      'onlineBookingFee',
      'nationalSiteCommission',
      'selectBookingFee',
      'creditCardFee',
      'rideInsuranceFee',
      'weatherGuaranteeFee',
      'vehiclePaymentPlanFee'
    ])
  }),
  z.object({
    type: z.literal('PlatformFeeSurrenderReversal')
  }),
  z.object({
    type: z.literal('StripeUncategorizedBalanceTransaction'),
    stripeId: z.string(),
    platformBalanceTransactionId: z.string().nullable(),
    connectBalanceTransactionId: z.string().nullable()
  }),
  z.object({
    type: z.literal('GiftCardCreation') // when gift card is initially created, from outfitter gift card account to a gift card
  }),
  z.object({
    type: z.literal('GiftCardManualValueAdjustment') // when gift card value is changed, from gift card to outfitter gift card account or vice versa
  }),
  z.object({
    type: z.literal('GiftCardPayment') // from gift card to customer
  }),
  z.object({
    type: z.literal('GiftCardPaymentReversal') // from customer to gift card, a gift card refund
  }),
  z.object({
    type: z.literal('GiftCardRedemption'), // from outfitter to outfitter gift card account, reduces gift card liability. Created along with a gift card payment.
    giftCardShortId: z.string()
  }),
  z.object({
    type: z.literal('GiftCardRedemptionReversal') // from outfitter gift card account to outfitter account. Created along with GiftCardPaymentReversal
  })
])

export const transferMetadataTypeOptions = transferMetadata.options.map((o) => o.shape.type.value)
export type transferMetadataType = (typeof transferMetadataTypeOptions)[number]
export const transferMetadataTypes = enumOptionsToObject(transferMetadataTypeOptions)

export const transferMetadataTypeEnum = z.enum(
  transferMetadataTypeOptions as [string, ...string[]]
) as z.ZodSchema<transferMetadataType>

export type transferMetadata = z.infer<typeof transferMetadata>

export type TransferMetadataGeneric<T extends transferMetadataType> = Extract<transferMetadata, { type: T }>

/**
 * This function is mostly here to help with typescript types inferring correctly. Use as
 *
 * ```ts
 * transfers.filter(isTransferOfType('StripePayment'))
 * ```
 */
export function isTransferOfTypeFilter<Transfer extends { metadata: transferMetadata }, Type extends transferMetadataType>(
  type: Type
) {
  return function (transfer: Transfer): transfer is Transfer & { metadata: TransferMetadataGeneric<Type> } {
    return transfer.metadata.type === type
  }
}

export function isTransferAmongTypesFilter<
  Transfer extends { metadata: transferMetadata },
  Type extends transferMetadataType
>(types: readonly Type[]) {
  return function (transfer: Transfer): transfer is Transfer & { metadata: TransferMetadataGeneric<Type> } {
    return (types as readonly transferMetadataType[]).includes(transfer.metadata.type)
  }
}
